/**
 * Authentication and Authorization Error Handler
 *
 * Provides centralized handling for HTTP authentication/authorization errors:
 * - 401 UNAUTHORIZED: Invalid/expired token - logout required
 * - 403 FORBIDDEN: Valid token but insufficient permissions - show error
 * - 500 INTERNAL_SERVER_ERROR: Server error - show generic error
 */

import {showSnackbar} from '../components/SnackBar/SnackBar';

/**
 * Handle 401 Unauthorized - Token is invalid, missing, or expired
 * Logs out the user and redirects to login page
 * @param {string} customMessage - Optional custom message to show
 */
export const handle401Unauthorized = (customMessage) => {
    const message = customMessage || "Your session has expired. Please log in again.";

    showSnackbar({
        title: "Session Expired",
        message: message,
        type: "warning",
        position: {vertical: "top", horizontal: "center"},
    });

    // Small delay to allow user to see the message
    setTimeout(() => {
        localStorage.clear();
        window.location.href = "/";
    }, 1500);
};

/**
 * Handle 403 Forbidden - User is authenticated but lacks permission
 * Shows error message but keeps user logged in
 * @param {string} customMessage - Optional custom message to show
 */
export const handle403Forbidden = (customMessage) => {

    const message = customMessage || "You do not have permission to perform this action.";

    showSnackbar({
        title: "Permission Denied",
        message: message,
        type: "error",
        position: {vertical: "bottom", horizontal: "right"},
    });
};

/**
 * Handle 500 Internal Server Error
 * Shows generic error message
 * @param {string} customMessage - Optional custom message to show
 * @param {Error} error - Optional error object for logging
 */
export const handle500ServerError = (customMessage, error) => {
    const message = customMessage || "An unexpected error occurred. Please try again later.";

    if (error) {
        console.error("Server error:", error);
    }

    showSnackbar({
        title: "Server Error",
        message: message,
        type: "error",
        position: {vertical: "bottom", horizontal: "right"},
    });
};

/**
 * Generic handler for HTTP response status codes
 * Routes to appropriate handler based on status code
 * @param {Response} response - Fetch API response object
 * @param {Object} customMessages - Optional custom messages for each status code
 * @returns {boolean} - Returns true if error was handled, false if no error
 */
export const handleHttpError = (response, customMessages = {}) => {
    const {status} = response;

    switch (status) {
        case 401:
            handle401Unauthorized(customMessages['401']);
            return true;

        case 403:
            handle403Forbidden(customMessages['403']);
            return true;

        case 500:
            handle500ServerError(customMessages['500']);
            return true;

        default:
            return false;
    }
};

/**
 * Check response status and handle errors
 * Use this as a middleware in your fetch chain
 * @param {Response} response - Fetch API response object
 * @returns {Response} - Returns the response if no error, throws if error handled
 */
export const checkResponseStatus = (response) => {
    if (response.status === 401) {
        handle401Unauthorized();
        throw new Error('Unauthorized');
    }

    if (response.status === 403) {
        handle403Forbidden();
        // Don't throw for 403 - let the component handle the response
        return response;
    }

    if (response.status === 500) {
        handle500ServerError();
        throw new Error('Server Error');
    }

    return response;
};
